const CHUNK_SIZE = 16384;
/**
 * An EventListenerObject that handles receiving application bundles.
 * Use a single instance of this listener per bundle and remove the listener
 * using RTCDataChannel.removeEventListener('message', instance) when done.
 */
class ReceiveBundleEventListener {
    constructor(manifest, expectedModuleCount, doneCallback) {
        this.manifest = manifest;
        this.expectedModuleCount = expectedModuleCount;
        this.doneCallback = doneCallback;
        this.modules = [];
        this.modulesMeta = [];
        this.receiveBuffer = [];
        this.receivedSize = 0;
        this.moduleCount = 0;
    }
    handleEvent(event) {
        const messageData = event.data;
        if (typeof (messageData) === 'string') {
            // We received new meta data.
            this.currentMetaData = JSON.parse(messageData);
            return;
        }
        else if (messageData instanceof ArrayBuffer) {
            this.receiveBuffer.push(messageData);
            this.receivedSize += messageData.byteLength;
            // console.log(`Received file chunk: ${event.data.byteLength} bytes (total: ${receivedSize}).`);
        }
        else if (messageData instanceof Blob) {
            throw 'Receiving Blob data is not implemented.';
        }
        if (this.currentMetaData && this.receivedSize === this.currentMetaData.fileInfo.size) {
            // console.log(`FileTransfer: received '${currentMetaData.fileInfo.name}' (${currentMetaData.fileInfo.size} bytes).`);
            // The current module is done.
            const module = new Blob(this.receiveBuffer, { type: this.currentMetaData.fileInfo.type });
            this.modulesMeta.push(this.currentMetaData);
            this.modules.push(module);
            this.moduleCount++;
            if (this.moduleCount === this.expectedModuleCount) {
                this.doneCallback({ manifest: this.manifest, modulesMeta: this.modulesMeta, modules: this.modules });
            }
            else {
                // Reset for the next file
                this.receiveBuffer.length = 0;
                this.receivedSize = 0;
            }
        }
    }
}
export class WebRtcFileTransfer {
    static sendBundle(bundle, dirtyOnly, dataChannel) {
        if (bundle.modules.length !== bundle.modulesMeta.length)
            throw `The module meta data length does not match the number of modules in the application bundle.`;
        // Trick for sequentially resolving promises
        return bundle.modulesMeta.reduce((accumulatorPromise, meta, i) => {
            return accumulatorPromise.then(() => {
                if (dirtyOnly && !meta.isDirty)
                    return accumulatorPromise;
                const module = bundle.modules[i];
                return this.sendModule(meta, module, dataChannel);
            });
        }, Promise.resolve());
    }
    static sendModule(meta, module, dataChannel) {
        let offset = 0;
        const fileReader = new FileReader();
        // Send meta data first
        dataChannel.send(JSON.stringify(meta));
        const readSlice = (o) => {
            const slice = module.slice(offset, o + CHUNK_SIZE);
            fileReader.readAsArrayBuffer(slice);
        };
        return new Promise((resolve, reject) => {
            fileReader.addEventListener('error', error => { reject(`Error reading file: ${error}`); });
            fileReader.addEventListener('abort', event => { reject(`File reading aborted: ${event}`); });
            fileReader.addEventListener('load', e => {
                // console.log('FileRead.onload ', e);
                const buffer = e.target.result;
                dataChannel.send(buffer);
                offset += buffer.byteLength;
                if (offset < module.size) {
                    readSlice(offset);
                }
                else
                    resolve(); // done
            });
            readSlice(0); // go
        });
    }
    static receiveBundle(manifest, expectedModuleCount, channel) {
        const promise = new Promise((resolve, reject) => {
            // Set up an event listener which we can remove when done,
            const listener = new ReceiveBundleEventListener(manifest, expectedModuleCount, (bundle) => {
                // Done...
                channel.removeEventListener('message', listener);
                resolve(bundle);
            });
            // Go
            channel.addEventListener('message', listener);
            channel.onerror = (ev /* : RTCErrorEvent/ */) => {
                reject(ev);
            };
        });
        return promise;
    }
}
//# sourceMappingURL=web-rtc-file-transfer.js.map